import type { ExecutionsHistoryScheduleSection } from '@src/models/interfaces/executionsHistoryDashboard.interface';

import type { ScheduleSummaryData } from './types';
import type { ScheduleSectionsState } from './hooks/useScheduleHistorySections';
import { getComplianceRangeFilter } from './utils';

const toNumberOrZero = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeProgressItem = <T extends { value: number }>(item: T): T => ({
  ...item,
  value: toNumberOrZero(item.value),
});

/** Adapts the sectioned services payloads to the existing Cronogramas view model. */
export const buildScheduleSummaryData = (sections: ScheduleSectionsState): ScheduleSummaryData => {
  const getData = (section: ExecutionsHistoryScheduleSection) => {
    const result = sections[section].result;
    return result?.status === 'ok'
      ? result.response.data as Partial<ScheduleSummaryData>
      : {};
  };
  const overview = getData('schedule-overview');
  const compliance = getData('schedule-compliance');
  const events = getData('schedule-events');
  const users = getData('schedule-users');
  const divisions = getData('schedule-divisions');

  const metrics = (overview.metrics ?? []).map(metric => ({
    ...metric,
    value: metric.value ?? '0',
    detailValues: Object.fromEntries(
      Object.entries(metric.detailValues ?? {}).map(([key, value]) => [key, toNumberOrZero(value)]),
    ),
  }));
  const results = (overview.results ?? []).map(result => ({
    ...result,
    value: toNumberOrZero(result.value),
    percentage: toNumberOrZero(result.percentage),
  }));
  const period = (overview.period ?? []).map(point => ({
    ...point,
    completion: toNumberOrZero(point.completion),
    expected: toNumberOrZero(point.expected),
    completed: toNumberOrZero(point.completed),
    executionExpected: toNumberOrZero(point.executionExpected),
    executionCompleted: toNumberOrZero(point.executionCompleted),
  }));
  const scheduleCompletionRanges = (compliance.scheduleCompletionRanges ?? []).map(range => ({
    ...range,
    value: toNumberOrZero(range.value),
    schedules: (range.schedules ?? []).map(schedule => ({
      ...schedule,
      completion: toNumberOrZero(schedule.completion),
      completed: toNumberOrZero(schedule.completed),
      total: toNumberOrZero(schedule.total),
    })),
    drilldownFilter: getComplianceRangeFilter(range.key),
  }));
  const eventComplianceRanges = (events.eventComplianceRanges ?? []).map(range => ({
    ...range,
    value: toNumberOrZero(range.value),
    eventTypes: (range.eventTypes ?? []).map(eventType => ({
      ...eventType,
      completion: toNumberOrZero(eventType.completion),
      completed: toNumberOrZero(eventType.completed),
      total: toNumberOrZero(eventType.total),
    })),
    drilldownFilter: getComplianceRangeFilter(range.key),
  }));
  const completionRanges = (users.completionRanges ?? []).map(range => ({
    ...range,
    value: toNumberOrZero(range.value),
    users: (range.users ?? []).map(user => ({
      ...user,
      completion: toNumberOrZero(user.completion),
      completed: toNumberOrZero(user.completed),
      total: toNumberOrZero(user.total),
      schedules: toNumberOrZero(user.schedules),
    })),
    drilldownFilter: getComplianceRangeFilter(range.key),
  }));

  return {
    metrics,
    results,
    period,
    scheduleCompletionRanges,
    eventComplianceRanges,
    analyzedEventTypes: toNumberOrZero(events.analyzedEventTypes),
    eventTypes: (events.eventTypes ?? [])
      .map(normalizeProgressItem)
      .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label)),
    completionRanges,
    indicators: users.indicators ?? [],
    divisionCompliance: (divisions.divisionCompliance ?? []).map(normalizeProgressItem),
    divisionScheduleAssignments: (divisions.divisionScheduleAssignments ?? []).map(normalizeProgressItem),
    userSummary: {
      assigned: toNumberOrZero(users.userSummary?.assigned),
      active: toNumberOrZero(users.userSummary?.active),
      inactive: toNumberOrZero(users.userSummary?.inactive),
    },
    totalSchedules: toNumberOrZero(overview.totalSchedules),
    completionRate: toNumberOrZero(overview.completionRate),
    executionSummary: {
      completed: toNumberOrZero(overview.executionSummary?.completed),
      total: toNumberOrZero(overview.executionSummary?.total),
    },
  };
};

export const buildScheduleUserMetricValues = (data: ScheduleSummaryData) => {
  const users = data.completionRanges.flatMap(range => range.users);
  const hasRouteUserStatuses =
    data.userSummary.complete !== undefined && data.userSummary.partial !== undefined;
  return {
    assigned: users.length,
    active: hasRouteUserStatuses
      ? data.userSummary.active
      : users.filter(user => user.completion > 0).length,
    complete: data.userSummary.complete ?? users.filter(user => user.completion === 100).length,
    partial: data.userSummary.partial ?? users.filter(user => user.completion > 0 && user.completion < 100).length,
    inactive: hasRouteUserStatuses
      ? data.userSummary.inactive
      : users.filter(user => user.completion === 0).length,
    average: users.length
      ? Number((users.reduce((sum, user) => sum + (user.schedules ?? 0), 0) / users.length).toFixed(1))
      : 0,
  };
};
